import * as React from "react";
import {
    BuildingOfficeIcon,
    DocumentCurrencyEuroIcon,
    EnvelopeIcon,
    IdentificationIcon,
    MapPinIcon,
    PencilIcon,
    PhoneIcon,
} from '@heroicons/react/24/outline'
import {
    Customer,
    customerAvatarColor,
    customerInitials,
    formatCustomerAddress,
} from "../../../../../api/customers/Customer.ts";

function ContactRow({Icon, children}: { Icon: typeof MapPinIcon, children: React.ReactNode }) {
    return (
        <div className="flex items-start gap-2.5 text-sm text-gray-700">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"/>
            <span className="min-w-0 break-words">{children}</span>
        </div>
    )
}

interface CustomerCardProps {
    customer: Customer
    onEdit: (customer: Customer) => void
    onCreateInvoice: (customer: Customer) => void
}

export default function CustomerCard({customer, onEdit, onCreateInvoice}: CustomerCardProps) {
    const formattedAddress = formatCustomerAddress(customer.address)

    return (
        <div className="group flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex items-start gap-3">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-white ${customerAvatarColor(customer)}`}>
                    {customerInitials(customer)}
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold text-gray-900" title={customer.company_name}>
                        {customer.company_name}
                    </h3>
                    <p className="truncate text-sm text-gray-500">
                        {customer.address.city || ' '}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => onEdit(customer)}
                    className="rounded-md p-2 text-gray-400 ring-1 ring-inset ring-gray-200 transition hover:bg-gray-50 hover:text-primary"
                    aria-label={`${customer.company_name} bewerken`}
                >
                    <PencilIcon className="h-4 w-4"/>
                </button>
            </div>

            <div className="mt-4 flex flex-col gap-2">
                {customer.address_extra_line && <ContactRow Icon={BuildingOfficeIcon}>{customer.address_extra_line}</ContactRow>}
                {formattedAddress && <ContactRow Icon={MapPinIcon}>{formattedAddress}</ContactRow>}
                {customer.email && (
                    <ContactRow Icon={EnvelopeIcon}>
                        {customer.email}
                        {customer.fixed_cc_email && <span className="text-gray-400"> · CC {customer.fixed_cc_email}</span>}
                    </ContactRow>
                )}
                {customer.phone_number && <ContactRow Icon={PhoneIcon}>{customer.phone_number}</ContactRow>}
                {customer.vat_number && <ContactRow Icon={IdentificationIcon}>{customer.vat_number}</ContactRow>}
            </div>

            <div className="mt-4 flex flex-1 items-end justify-between border-t border-gray-100 pt-4">
                {customer.stats ? (
                    <div>
                        <div className="text-xs text-gray-500">Facturen</div>
                        <div className="text-sm font-semibold text-gray-900">{customer.stats.invoice_count}</div>
                    </div>
                ) : (
                    <div className="text-xs text-gray-400">Nog geen facturen</div>
                )}

                <button
                    type="button"
                    onClick={() => onCreateInvoice(customer)}
                    className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-primary transition hover:bg-gray-50"
                >
                    <DocumentCurrencyEuroIcon className="h-4 w-4"/>
                    Factuur maken
                </button>
            </div>
        </div>
    )
}
